// The "Custom fields" section of the Opportunities column picker (the Projects list shows its fields as a
// nested group instead, so does not use this), rendered inside TheEye's panel through
// its opt-in renderExtraSection. Renders <li>s because it lives in TheEye's <ul>, and reuses TheEye's own
// `.name-expand-wrapper` / `.visible-state-icon` / `.eye-section-heading` markup so the eye icons and the
// heading match the native rows exactly.
//
// 8 or fewer fields are listed inline. More collapse into one summary row, "Custom fields (N)" plus
// "X shown", which opens a fly-out to the LEFT (the picker sits at the page's right edge), 8 rows tall
// and scrolling. The fly-out's bottom lines up with the summary row's, so it grows upward from the foot of
// the panel and stays on screen. TheEye ignores scrolls inside its panel, so scrolling the fly-out does not
// close it. Row height, text and hover come from the panel's own style (columnPickerPanelStyle).
//
// Staying open: TheEye closes its panel when focus moves to an element whose id does not contain
// `card-layout-list-option`. Every focusable control here carries such an id, so keyboard focus can move
// in from a native option and between the controls, and each reports its own blur back to TheEye
// (onBlur) so focus leaving the picker still closes it. A mouse press keeps focus where it is
// (mousedown default prevented), so a click never blurs the panel shut.
import React, {useState} from 'react';
import Styled from 'styled-components';
import {tokensForThemeProvider} from '@forecast-it/design-system';
import type {CustomFieldColumnDef} from './customFieldColumnModel';
import {COLUMN_PICKER_HOVER_BACKGROUND, COLUMN_PICKER_ROW_HEIGHT_PX} from './columnPickerPanelStyle';

export const INLINE_LIMIT = 8;
const OPTION_ID_PREFIX = 'card-layout-list-option-cf-';

const color = tokensForThemeProvider.sky.sys.color;

export interface CustomFieldColumnSectionLabels {
	heading: string;
	showAll: string;
	hideAll: string;
	summary: (count: number) => string;
	shown: (count: number) => string;
}
export interface CustomFieldColumnSectionProps {
	/** The available definitions, A-Z. */
	definitions: CustomFieldColumnDef[];
	shownIds: ReadonlySet<string>;
	onToggle: (id: string) => void;
	onShowAll: () => void;
	onHideAll: () => void;
	labels: CustomFieldColumnSectionLabels;
	/** TheEye's blur handler: closes the picker unless focus moved to another of its options. */
	onBlur?: React.FocusEventHandler;
	/** Closes the picker (Escape). */
	onClose?: () => void;
}

const keepFocus = (e: React.MouseEvent): void => e.preventDefault();
const isActivateKey = (key: string): boolean => key === 'Enter' || key === ' ';

const Divider = Styled.li`
	&&& {
		height: 0;
		padding: 0;
		margin: 4px 0;
		border-top: 1px solid ${color.border.decorative};
		background: none;
		cursor: default;
	}
`;
// The panel highlights every <li> on hover; the heading row is not an option, so it opts out.
const Heading = Styled.li`
	&&& {
		align-items: baseline;
		padding: 0 16px 0 0;
		background: none;
		cursor: default;
	}
	button {
		border: none;
		background: none;
		padding: 0;
		color: ${color.text.content.primary};
		font: inherit;
		font-size: 13px;
		text-transform: none;
		cursor: pointer;
	}
`;
// Names exactly as the admin typed them (the panel capitalises its options), cut off with an ellipsis.
const Row = Styled.li`
	text-transform: none;
	.name {
		min-width: 0;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
`;
// The label in the brand colour; grey while its fly-out is open, like a hovered option.
const Summary = Styled(Row)`
	&[aria-expanded='true'] {
		background-color: ${COLUMN_PICKER_HOVER_BACKGROUND};
	}
	/* Child combinators: the fly-out's rows are nested inside this <li> and must keep the main text colour. */
	> .name-expand-wrapper > .name {
		color: ${color.text.content.primary};
	}
	> .name-expand-wrapper > .count {
		color: ${color.text.content.auxiliary};
		margin-left: 8px;
		white-space: nowrap;
	}
`;
// The fly-out sits inside the summary <li>, whose hover or focus colour would otherwise cascade into every
// fly-out row; an explicit main text colour stops that. A hovered or focused fly-out row is itself an <li>,
// so the panel's own rule still highlights it exactly like a native option.
// Anchored by its bottom edge to the summary row's (-1px covers the panel border), so it grows upward.
const Flyout = Styled.ul`
	position: absolute;
	right: 100%;
	bottom: -1px;
	width: 240px;
	max-height: ${INLINE_LIMIT * COLUMN_PICKER_ROW_HEIGHT_PX}px;
	overflow-y: auto;
	margin: 0 4px 0 0;
	padding: 0;
	list-style: none;
	color: ${color.text.content.main};
	background: ${color.background.neutral[1]};
	border: 1px solid ${color.border.decorative};
	border-radius: 4px;
	box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.05);
`;

const FieldRow = ({
	def,
	shown,
	onToggle,
	onBlur,
	onKeyDown,
}: {
	def: CustomFieldColumnDef;
	shown: boolean;
	onToggle: (id: string) => void;
	onBlur?: React.FocusEventHandler;
	onKeyDown: (e: React.KeyboardEvent, activate: () => void) => void;
}) => (
	<Row
		id={`${OPTION_ID_PREFIX}${def.id}`}
		tabIndex={0}
		data-testid={`cf-picker-option-${def.id}`}
		data-cy={`eye-option-cf-${def.key}`}
		onMouseDown={keepFocus}
		onBlur={onBlur}
		onKeyDown={e => onKeyDown(e, () => onToggle(def.id))}
		onClick={e => {
			e.stopPropagation();
			onToggle(def.id);
		}}
	>
		<div className="name-expand-wrapper">
			<div className="name" title={def.displayName}>
				{def.displayName}
			</div>
			<div className={'visible-state-icon' + (shown ? ' selected' : '')} />
		</div>
	</Row>
);

export const CustomFieldColumnSection = ({
	definitions,
	shownIds,
	onToggle,
	onShowAll,
	onHideAll,
	labels,
	onBlur,
	onClose,
}: CustomFieldColumnSectionProps): JSX.Element | null => {
	const [flyoutOpen, setFlyoutOpen] = useState(false);
	if (definitions.length === 0) return null;
	const shownCount = definitions.filter(d => shownIds.has(d.id)).length;
	const allShown = shownCount === definitions.length;

	// Enter / Space activate the focused control; Escape closes the picker, as it does on a native option.
	const handleKeyDown = (e: React.KeyboardEvent, activate: () => void): void => {
		if (e.key === 'Escape') {
			e.stopPropagation();
			onClose?.();
		} else if (isActivateKey(e.key)) {
			e.preventDefault();
			e.stopPropagation();
			activate();
		}
	};
	const toggleAll = (): void => (allShown ? onHideAll() : onShowAll());

	const rows = definitions.map(def => (
		<FieldRow
			key={def.id}
			def={def}
			shown={shownIds.has(def.id)}
			onToggle={onToggle}
			onBlur={onBlur}
			onKeyDown={handleKeyDown}
		/>
	));

	return (
		<>
			<Divider aria-hidden="true" onMouseDown={keepFocus} />
			<Heading data-cy="cf-picker-heading" onMouseDown={keepFocus}>
				<span className="eye-section-heading">{labels.heading}</span>
				<button
					type="button"
					id={`${OPTION_ID_PREFIX}toggle-all`}
					data-cy="cf-picker-toggle-all"
					onBlur={onBlur}
					// A native button already turns Enter / Space into a click, so only Escape is handled here.
					onKeyDown={e => {
						if (e.key === 'Escape') {
							e.stopPropagation();
							onClose?.();
						}
					}}
					onClick={e => {
						e.stopPropagation();
						toggleAll();
					}}
				>
					{allShown ? labels.hideAll : labels.showAll}
				</button>
			</Heading>
			{definitions.length <= INLINE_LIMIT ? (
				rows
			) : (
				<Summary
					id={`${OPTION_ID_PREFIX}summary`}
					tabIndex={0}
					aria-expanded={flyoutOpen}
					data-testid="cf-picker-summary"
					data-cy="cf-picker-summary"
					onMouseDown={keepFocus}
					onBlur={e => {
						// A fly-out row reports its own blur; React bubbles it here too.
						if (e.target === e.currentTarget) onBlur?.(e);
					}}
					// A fly-out row stops the keys it handles, so they never reach this row.
					onKeyDown={e => handleKeyDown(e, () => setFlyoutOpen(open => !open))}
					onClick={e => {
						e.stopPropagation();
						setFlyoutOpen(open => !open);
					}}
				>
					<div className="name-expand-wrapper">
						<div className="expand-nested-options-button-left" aria-hidden="true" />
						<div className="name">{labels.summary(definitions.length)}</div>
						<span className="count">{labels.shown(shownCount)}</span>
					</div>
					{flyoutOpen && (
						<Flyout data-testid="cf-picker-flyout" data-cy="cf-picker-flyout" onClick={e => e.stopPropagation()}>
							{rows}
						</Flyout>
					)}
				</Summary>
			)}
		</>
	);
};
